import { Zap, TrendingUp, Clock, AlertTriangle, ChevronRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { getDaysUntilReset, formatCredits } from "@/lib/credit-calculator";
import { useNavigate } from "react-router-dom";

interface CreditBalanceWidgetProps {
  compact?: boolean;
}

export function CreditBalanceWidget({ compact = false }: CreditBalanceWidgetProps) {
  const { credits, tier } = useSubscription();
  const navigate = useNavigate();
  const balance = credits.balance;

  if (!balance) {
    return null;
  }

  const monthlyPercentUsed = balance.monthlyCredits > 0 
    ? (balance.monthlyUsed / balance.monthlyCredits) * 100 
    : 0;
  
  const daysUntilReset = getDaysUntilReset(balance.monthlyResetAt);
  const isLow = balance.totalRemaining < 10;

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <Zap className="h-4 w-4 text-primary" />
        <span className={isLow ? "text-destructive font-medium" : ""}>
          {formatCredits(balance.totalRemaining)} credits
        </span>
        {isLow && (
          <Badge variant="destructive" className="text-xs">Low</Badge>
        )}
      </div>
    );
  }

  return (
    <Card className={`glass ${isLow ? "border-destructive/50" : ""}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Credit Balance
          </div>
          {isLow && (
            <Badge variant="destructive" className="flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Low
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Total Available */}
        <div className="text-center py-2">
          <div className="text-4xl font-bold text-primary">
            {formatCredits(balance.totalRemaining)}
          </div>
          <p className="text-sm text-muted-foreground">credits available</p>
        </div>

        {/* Monthly Credits Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">Monthly</Badge>
            </div>
            <span className="font-medium">
              {formatCredits(balance.monthlyCredits - balance.monthlyUsed)} / {formatCredits(balance.monthlyCredits)}
            </span>
          </div>
          <Progress value={monthlyPercentUsed} className="h-2" />
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            Resets in {daysUntilReset} day{daysUntilReset !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Top-up Credits */}
        {balance.topupCredits > 0 && (
          <div className="flex items-center justify-between text-sm pt-2 border-t">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Top-up Credits</span>
            </div>
            <span className="font-medium">{formatCredits(balance.topupCredits)}</span>
          </div>
        )}

        {/* Actions */}
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-between"
          onClick={() => navigate(tier === "free" ? "/pricing" : "/settings?tab=billing")}
        >
          <span>{tier === "free" ? "Upgrade Plan" : "Manage Credits"}</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
